import React, { useState, useMemo } from 'react';
import { MessageSquare, Mail, RefreshCw, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import ContactsSearchBar from './contacts/ContactsSearchBar';
import ContactsList from './contacts/ContactsList';
import MessageDetailModal from './contacts/MessageDetailModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactMessagesTab = ({
  contacts,
  setContacts,
  loading,
  sortOrder,
  onRefresh,
  onExport,
  formatDate,
  staffProfiles,
}) => {
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  const [replyStatus, setReplyStatus] = useState(null);
  const [selectedSender, setSelectedSender] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // ---------------- Filtering + sorting ----------------
  const filteredContacts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return contacts.filter((c) => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (!q) return true;
      const hay = `${c.name || ''} ${c.email || ''} ${c.phone || ''} ${c.subject || ''} ${c.message || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [contacts, searchQuery, statusFilter]);

  const sortedContacts = useMemo(() => {
    const copy = [...filteredContacts];
    copy.sort((a, b) => {
      const aVal = new Date(a.created_at);
      const bVal = new Date(b.created_at);
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return copy;
  }, [filteredContacts, sortOrder]);

  const unreadContacts = contacts.filter((c) => c.status === 'unread').length;

  // ---------------- Handlers ----------------
  const markAsRead = async (contactId) => {
    try {
      await fetch(`${API_URL}/api/contact/${contactId}/status?status=read`, { method: 'PATCH' });
      setContacts((prev) => prev.map((c) => (c.id === contactId ? { ...c, status: 'read' } : c)));
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.warn('Status update failed:', err.message);
    }
  };

  const openMessage = async (contact) => {
    setSelectedMessage(contact);
    setShowMessageModal(true);
    setReplyMessage('');
    setReplyStatus(null);
    if (contact.status === 'unread') await markAsRead(contact.id);
  };

  const toggleMessageStatus = async (contact, e) => {
    e.stopPropagation();
    const newStatus = contact.status === 'unread' ? 'read' : 'unread';
    try {
      await fetch(`${API_URL}/api/contact/${contact.id}/status?status=${newStatus}`, { method: 'PATCH' });
      setContacts((prev) => prev.map((c) => (c.id === contact.id ? { ...c, status: newStatus } : c)));
      if (selectedMessage?.id === contact.id) {
        setSelectedMessage({ ...contact, status: newStatus });
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.warn('Toggle status failed:', err.message);
    }
  };

  const deleteMessage = async (contact, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Delete message from ${contact.name}? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/contact/${contact.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Failed to delete');
      setContacts((prev) => prev.filter((c) => c.id !== contact.id));
      if (selectedMessage?.id === contact.id) {
        setShowMessageModal(false);
        setSelectedMessage(null);
      }
    } catch (err) {
      window.alert(err.message || 'Failed to delete message.');
    }
  };

  const sendReply = async () => {
    if (!replyMessage.trim() || !selectedMessage) return;
    setReplyLoading(true);
    setReplyStatus(null);
    try {
      const response = await fetch(`${API_URL}/api/email/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to_email: selectedMessage.email,
          to_name: selectedMessage.name,
          subject: `Re: ${selectedMessage.subject}`,
          message: replyMessage,
          original_message: selectedMessage.message,
          sender_id: selectedSender,
        }),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.detail || 'Failed to send reply');

      setReplyStatus({ type: 'success', message: 'Reply sent successfully!' });
      setReplyMessage('');
      await fetch(`${API_URL}/api/contact/${selectedMessage.id}/status?status=replied`, { method: 'PATCH' });
      setContacts((prev) => prev.map((c) => (c.id === selectedMessage.id ? { ...c, status: 'replied' } : c)));
      setSelectedMessage((prev) => ({ ...prev, status: 'replied' }));
    } catch (err) {
      setReplyStatus({ type: 'error', message: err.message || 'Failed to send reply.' });
    } finally {
      setReplyLoading(false);
    }
  };

  // ---------------- Render ----------------
  return (
    <>
      <div className="stats-grid">
        <Card className="stat-card total-card">
          <CardContent>
            <div className="stat-content">
              <MessageSquare className="stat-icon" size={32} />
              <div>
                <div className="stat-value">{contacts.length}</div>
                <div className="stat-label">Total Messages</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent>
            <div className="stat-content">
              <Mail className="stat-icon" size={32} />
              <div>
                <div className="stat-value">{unreadContacts}</div>
                <div className="stat-label">Unread</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="actions-bar">
        <Button onClick={onRefresh} className="action-btn refresh-btn" disabled={loading}>
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          Refresh
        </Button>
        <Button onClick={onExport} className="action-btn export-btn" disabled={contacts.length === 0}>
          <Download size={18} />
          Export CSV
        </Button>
      </div>

      <ContactsSearchBar
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        filteredCount={sortedContacts.length}
        totalCount={contacts.length}
      />

      <ContactsList
        loading={loading}
        contacts={contacts}
        sortedContacts={sortedContacts}
        formatDate={formatDate}
        onOpen={openMessage}
        onToggleStatus={toggleMessageStatus}
        onDelete={deleteMessage}
      />

      {showMessageModal && selectedMessage && (
        <MessageDetailModal
          message={selectedMessage}
          formatDate={formatDate}
          staffProfiles={staffProfiles}
          selectedSender={selectedSender}
          setSelectedSender={setSelectedSender}
          replyMessage={replyMessage}
          setReplyMessage={setReplyMessage}
          replyLoading={replyLoading}
          replyStatus={replyStatus}
          onClose={() => setShowMessageModal(false)}
          onSendReply={sendReply}
          onToggleStatus={toggleMessageStatus}
          onDelete={deleteMessage}
        />
      )}
    </>
  );
};

export default ContactMessagesTab;
